import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
  type User as FirebaseUser,
} from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { errorEvents } from '@/lib/error-events';

interface User {
  id: string;
  username: string;
  email: string;
  avatar?: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, displayName?: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/** Map a Firebase user onto the app's lightweight User shape. */
function toAppUser(fbUser: FirebaseUser): User {
  const email = fbUser.email ?? '';
  return {
    id: fbUser.uid,
    email,
    username: fbUser.displayName || email.split('@')[0] || 'User',
    avatar: fbUser.photoURL ?? undefined,
  };
}

/**
 * Turn a Firebase auth error into a human-readable message. Firebase surfaces
 * most credential problems as the deliberately-vague `auth/invalid-credential`.
 */
function authErrorMessage(error: unknown): string {
  const code = (error as { code?: string })?.code ?? '';
  switch (code) {
    case 'auth/invalid-email':
      return 'That email address is not valid.';
    case 'auth/user-disabled':
      return 'This account has been disabled.';
    case 'auth/user-not-found':
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
      return 'Incorrect email or password.';
    case 'auth/email-already-in-use':
      return 'An account with that email already exists.';
    case 'auth/weak-password':
      return 'Password should be at least 6 characters.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Please wait a moment and try again.';
    case 'auth/network-request-failed':
      return 'Network error reaching the authentication server.';
    default:
      return (error as { message?: string })?.message || 'Authentication failed.';
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Subscribe to Firebase auth state; this fires immediately with the restored
  // session (if any) on mount, then on every login/logout.
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (fbUser) => {
      setUser(fbUser ? toAppUser(fbUser) : null);
      setIsLoading(false);
    });
    return unsubscribe;
  }, []);

  const isAuthenticated = user !== null;

  const login = async (email: string, password: string) => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
      // onAuthStateChanged updates `user`.
    } catch (error) {
      errorEvents.emit(authErrorMessage(error), 'Login Error');
      throw error;
    }
  };

  const register = async (email: string, password: string, displayName?: string) => {
    try {
      const credential = await createUserWithEmailAndPassword(auth, email, password);
      if (displayName) {
        await updateProfile(credential.user, { displayName });
        setUser(toAppUser(credential.user));
      }
      // createUserWithEmailAndPassword signs the new user in automatically.
    } catch (error) {
      errorEvents.emit(authErrorMessage(error), 'Registration Error');
      throw error;
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      errorEvents.emit(authErrorMessage(error), 'Logout Error');
      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{ user, isAuthenticated, isLoading, login, register, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
